import React, { useState, useCallback, useMemo } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FileText, FileSpreadsheet, FileCode, X, GitMerge,
  BarChart3, Package, Wrench, Cpu, Box, Users, Target,
  Factory, Clock, LayoutDashboard, Settings, Sparkles,
  Link2, Network, Share2, Database, Server, Cloud
} from 'lucide-react';

// ============================================
// THEME
// ============================================
const T = {
  bg:'#0a0e17', s:'#111827', s2:'#1a2235', s3:'#1e293b', b:'#1e293b',
  t:'#e2e8f0', t2:'#94a3b8', t3:'#64748b', p:'#3b82f6', pl:'#60a5fa',
  g:'#10b981', gl:'#34d399', w:'#f59e0b', wl:'#fbbf24', d:'#ef4444',
  pp:'#8b5cf6', ppl:'#a78bfa', te:'#14b8a6', tel:'#2dd4bf', pk:'#ec4899',
  cy:'#06b6d4', in:'#6366f1', or:'#f97316',
  conventional:'#64748b', casting:'#f59e0b', cnc:'#3b82f6', printing:'#8b5cf6', robotics:'#10b981'
};

const S = {
  card:{ background:T.s2, border:`1px solid ${T.b}`, borderRadius:'7px', padding:'9px', marginBottom:'6px' },
  cHdr:{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'5px', paddingBottom:'4px', borderBottom:`1px solid ${T.b}` },
  cTtl:{ fontSize:'0.82rem', fontWeight:600, display:'flex', alignItems:'center', gap:'4px' },
  badge:(c)=>({ display:'inline-block', padding:'1px 4px', borderRadius:'3px', fontSize:'0.58rem', fontWeight:600, background:`${c}22`, color:c }),
  grid:(n)=>({ display:'grid', gridTemplateColumns:`repeat(${n},1fr)`, gap:'4px' }),
  btn:{ padding:'4px 9px', borderRadius:'4px', border:'none', cursor:'pointer', fontSize:'0.74rem', fontWeight:600, display:'flex', alignItems:'center', gap:'3px', transition:'all 0.2s', whiteSpace:'nowrap' },
  bPri:{ background:`linear-gradient(135deg,${T.p},#2563eb)`, color:'white' },
  bSuc:{ background:`linear-gradient(135deg,${T.g},#059669)`, color:'white' },
  bWar:{ background:`linear-gradient(135deg,${T.w},#d97706)`, color:'white' },
  bDan:{ background:`linear-gradient(135deg,${T.d},#dc2626)`, color:'white' },
  bGho:{ background:'transparent', color:T.t2, border:`1px solid ${T.b}` },
  bPP:{ background:`linear-gradient(135deg,${T.pp},#7c3aed)`, color:'white' },
  bTeal:{ background:`linear-gradient(135deg,${T.te},#0d9488)`, color:'white' },
  statCard:{ background:T.s2, border:`1px solid ${T.b}`, borderRadius:'5px', padding:'6px', textAlign:'center' },
  statVal:{ fontSize:'1.1rem', fontWeight:700, color:T.pl },
  statLbl:{ fontSize:'0.55rem', color:T.t3, textTransform:'uppercase' },
  progressBar:{ height:'6px', background:T.b, borderRadius:'3px', overflow:'hidden', marginTop:'4px' },
  progressFill:{ height:'100%', borderRadius:'3px', transition:'width 0.3s ease' }
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const esc = (s) => (s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

const extractAllOperations = (bopData) => {
  if (!bopData) return [];
  if (bopData.productBOP) {
    const allOps = [];
    (bopData.productBOP.processes || []).forEach(process => {
      (process.plantBOPs || []).forEach(plantBOP => {
        (plantBOP.functionalGroups || []).forEach(fg => {
          (fg.operations || []).forEach(op => {
            allOps.push({
              ...op,
              functionalGroup: fg.name,
              plant: plantBOP.plant,
              process: process.name,
              processSequence: process.sequence,
              plantBOPName: plantBOP.name,
              fgSequence: fg.sequence,
              processType: process.type,
              plantBOPDescription: plantBOP.description
            });
          });
        });
      });
    });
    return allOps;
  }
  return (bopData.functionalGroups || []).flatMap(fg => 
    (fg.operations || []).map(op => ({
      ...op,
      functionalGroup: fg.name,
      plant: fg.plant || 'N/A'
    }))
  );
};

const extractFunctionalGroups = (bopData) => {
  if (!bopData) return [];
  if (bopData.productBOP) {
    const groups = [];
    (bopData.productBOP.processes || []).forEach(process => {
      (process.plantBOPs || []).forEach(plantBOP => {
        (plantBOP.functionalGroups || []).forEach(fg => {
          groups.push({
            ...fg,
            plant: plantBOP.plant,
            process: process.name,
            plantBOPName: plantBOP.name,
            processSequence: process.sequence
          });
        });
      });
    });
    return groups;
  }
  return bopData.functionalGroups || [];
};

const groupByTechnology = (equipment) => {
  const safeEquip = Array.isArray(equipment) ? equipment : [];
  const groups = { Conventional: [], Casting: [], CNC: [], '3D Printing': [], Robotics: [] };
  safeEquip.forEach(eq => {
    const tech = eq.technology || 'Conventional';
    if (groups[tech]) groups[tech].push(eq);
    else groups['Conventional'].push(eq);
  });
  return groups;
};

// ============================================
// MAIN REPORT COMPONENT
// ============================================
export default function Report({ cfg, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // SAFE DESTRUCTURING WITH DEFAULTS
  const { 
    prod = {},
    inc = {},
    ebom = [],
    mbom = [],
    bopData = { functionalGroups: [] },
    equip = [],
    tools = [],
    skills = [],
    qualityPlans = [],
    ewiDocs = [],
    wp = {},
    allOperations = [],
    functionalGroups = [],
    workCenters = []
  } = cfg || {};

  // SAFE DERIVED DATA
  const safeEbom = Array.isArray(ebom) ? ebom : [];
  const safeMbom = Array.isArray(mbom) ? mbom : [];
  const safeEquip = Array.isArray(equip) ? equip : [];
  const safeTools = Array.isArray(tools) ? tools : [];
  const safeSkills = Array.isArray(skills) ? skills : [];
  const safeQualityPlans = Array.isArray(qualityPlans) ? qualityPlans : [];
  const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
  const safeAllOperations = allOperations.length > 0 ? allOperations : extractAllOperations(bopData);
  const safeFunctionalGroups = functionalGroups.length > 0 ? functionalGroups : extractFunctionalGroups(bopData);
  const safeTechGroups = groupByTechnology(safeEquip);

  // ============================================
  // STATISTICS & ANALYTICS
  // ============================================
  const analytics = useMemo(() => {
    const totalEBOMQty = safeEbom.reduce((sum, e) => sum + (e.quantity || 1), 0);
    const totalMBOMQty = safeMbom.reduce((sum, m) => sum + (m.quantity || 1), 0);
    const totalStdTime = safeAllOperations.reduce((sum, op) => sum + (op.standardTime || op.stdTime || 0), 0);
    const totalSetupTime = safeAllOperations.reduce((sum, op) => sum + (op.setupTime || op.setup || 0), 0);
    const avgStdTime = safeAllOperations.length > 0 ? totalStdTime / safeAllOperations.length : 0;
    
    const wcCounts = {};
    safeAllOperations.forEach(op => {
      const wc = op.workCenter || op.wc || 'Unknown';
      wcCounts[wc] = (wcCounts[wc] || 0) + 1;
    });
    const topWCs = Object.entries(wcCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    
    const skillCounts = {};
    safeAllOperations.forEach(op => {
      if (op.skills) {
        const skillList = Array.isArray(op.skills) ? op.skills : op.skills.split(',').map(s => s.trim());
        skillList.forEach(s => {
          if (s) skillCounts[s] = (skillCounts[s] || 0) + 1;
        });
      }
    });
    const topSkills = Object.entries(skillCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    
    const toolCounts = {};
    safeAllOperations.forEach(op => {
      if (op.tools) {
        const toolList = Array.isArray(op.tools) ? op.tools : op.tools.split(',').map(t => t.trim());
        toolList.forEach(t => {
          if (t) toolCounts[t] = (toolCounts[t] || 0) + 1;
        });
      }
    });
    const topTools = Object.entries(toolCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    
    const opsWithQuality = safeAllOperations.filter(op => op.qualityPlanRef || op.inspection).length;
    const qualityCoverage = safeAllOperations.length > 0 ? (opsWithQuality / safeAllOperations.length * 100) : 0;
    
    const opsWithEWI = safeAllOperations.filter(op => op.workInstruction || op.ewiRef).length;
    const ewiCoverage = safeAllOperations.length > 0 ? (opsWithEWI / safeAllOperations.length * 100) : 0;
    
    const makeCount = safeEbom.filter(e => e.makeBuy === 'MAKE').length;
    const buyCount = safeEbom.filter(e => e.makeBuy === 'BUY').length;
    const makeRatio = safeEbom.length > 0 ? (makeCount / safeEbom.length * 100) : 0;
    
    const avgOpsPerFG = safeFunctionalGroups.length > 0 ? safeAllOperations.length / safeFunctionalGroups.length : 0;
    
    return {
      totalEBOMQty,
      totalMBOMQty,
      totalStdTime,
      totalSetupTime,
      avgStdTime,
      topWCs,
      topSkills,
      topTools,
      qualityCoverage,
      ewiCoverage,
      makeCount,
      buyCount,
      makeRatio,
      avgOpsPerFG,
      opsWithQuality,
      opsWithEWI,
      totalOperations: safeAllOperations.length,
      totalFGs: safeFunctionalGroups.length,
      totalEquipment: safeEquip.length,
      totalTools: safeTools.length,
      totalSkills: safeSkills.length,
      totalQualityPlans: safeQualityPlans.length,
      totalEWI: safeEwiDocs.length,
      totalEBOM: safeEbom.length,
      totalMBOM: safeMbom.length
    };
  }, [safeEbom, safeMbom, safeAllOperations, safeFunctionalGroups, safeEquip, safeTools, safeSkills, safeQualityPlans, safeEwiDocs]);

  // ============================================
  // ENHANCED DENDROGRAPH WITH RELATIONAL STRUCTURE
  // ============================================
  const generateDendrographText = useCallback((truncateForPreview = false) => {
    const MAX_PREVIEW = 30;
    
    let text = '╔══════════════════════════════════════════════════════════════════════════════════════════════════════════╗\n';
    text += '║                    WORK PACKAGE DIGITAL THREAD - RELATIONAL DENDROGRAPH                                 ║\n';
    text += '║                         Left Object → Right Object Relationship Mapping                                 ║\n';
    text += '╚══════════════════════════════════════════════════════════════════════════════════════════════════════════╝\n\n';
    
    // ============================================================
    // SECTION 1: WORK PACKAGE HEADER
    // ============================================================
    text += '╔══════════════════════════════════════════════════════════════════════════════════════════════════════════╗\n';
    text += `║  📋 WORK PACKAGE: ${(wp?.name || 'WP-GTC-001').padEnd(90)}\n`;
    text += `║  📍 Plant: ${(wp?.plant || 'JEDDAH').padEnd(30)}  👤 Owner: ${(wp?.owner || 'N/A').padEnd(30)}\n`;
    text += '╚══════════════════════════════════════════════════════════════════════════════════════════════════════════╝\n\n';

    // ============================================================
    // SECTION 2: EBOM RELATIONSHIPS
    // ============================================================
    text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
    text += '│  🔗 EBOM RELATIONSHIPS                                                                                   │\n';
    text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
    text += `  📐 EBOM: ${prod?.name || 'PRODUCT'} (${safeEbom.length} components)\n\n`;
    
    safeEbom.forEach((item, i) => {
      const isLast = i === safeEbom.length - 1;
      const prefix = isLast ? '  └──' : '  ├──';
      text += `  ${prefix} 🔩 ${item.findNumber || '--'}: ${item.name || 'Unnamed'}\n`;
      text += `  │      ├── [has quantity] → ${item.quantity || 1} ${item.uom || 'EA'}\n`;
      text += `  │      ├── [has revision] → ${item.revision || 'A'}\n`;
      text += `  │      ├── [make/buy] → ${item.makeBuy || 'BUY'}\n`;
      if (item.material) text += `  │      ├── [material] → ${item.material}\n`;
      if (item.weight) text += `  │      └── [weight] → ${item.weight} kg\n`;
      text += `  │\n`;
    });
    text += '\n';

    // ============================================================
    // SECTION 3: MBOM RELATIONSHIPS
    // ============================================================
    text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
    text += '│  🔗 MBOM RELATIONSHIPS                                                                                   │\n';
    text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
    text += `  📦 MBOM: ${prod?.mbomName || 'PRODUCT-M'} (${safeMbom.length} components)\n\n`;
    
    safeMbom.forEach((item, i) => {
      const isLast = i === safeMbom.length - 1;
      const prefix = isLast ? '  └──' : '  ├──';
      text += `  ${prefix} 📦 ${item.findNumber || '--'}: ${item.name || 'Unnamed'}\n`;
      text += `  │      ├── [quantity] → ${item.quantity || 1} ${item.uom || 'EA'}\n`;
      text += `  │      ├── [revision] → ${item.revision || 'A'}\n`;
      text += `  │      ├── [make/buy] → ${item.makeBuy || 'BUY'}\n`;
      text += `  │      └── [references operation] → ${item.operationReference || 'N/A'}\n`;
      text += `  │\n`;
    });
    text += '\n';

    // ============================================================
    // SECTION 4: BOP OPERATIONS WITH RELATIONAL STRUCTURE
    // ============================================================
    text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
    text += '│  ⚙️ BOP OPERATIONS - RELATIONAL STRUCTURE                                                               │\n';
    text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
    text += `  BOP: ${prod?.bopName || 'BOP-PRODUCT'} (${safeAllOperations.length} operations, ${safeFunctionalGroups.length} FGs)\n\n`;
    
    const opsToShow = truncateForPreview ? safeAllOperations.slice(0, MAX_PREVIEW) : safeAllOperations;
    
    opsToShow.forEach((op, opIndex) => {
      const isLastOp = opIndex === opsToShow.length - 1;
      const opPrefix = isLastOp ? '  └──' : '  ├──';
      
      // OPERATION HEADER
      text += `  ${opPrefix} 🔧 ${op.sequence || 'N/A'}: ${op.name || 'Unnamed'}\n`;
      text += `  │   ┌──────────────────────────────────────────────────────────────────────────────────────────────┐\n`;
      text += `  │   │  RELATIONSHIPS: ${op.name || 'Unnamed'} (LEFT) → (RIGHT)                                     │\n`;
      text += `  │   └──────────────────────────────────────────────────────────────────────────────────────────────┘\n`;
      
      // ============================================================
      // RESOURCE RELATIONSHIPS
      // ============================================================
      let hasResources = false;
      const resourceRelations = [];
      
      if (op.workCenter || op.wc) {
        resourceRelations.push({ left: 'Work Center', right: op.workCenter || op.wc, icon: '🏭' });
        hasResources = true;
      }
      if (op.machine) {
        resourceRelations.push({ left: 'Machine', right: op.machine, icon: '⚙️' });
        hasResources = true;
      }
      if (op.tooling) {
        resourceRelations.push({ left: 'Tooling', right: op.tooling, icon: '🔧' });
        hasResources = true;
      }
      
      if (hasResources) {
        text += `  │   ┌── 🏭 RESOURCE RELATIONSHIPS\n`;
        resourceRelations.forEach((rel, ri) => {
          const isLastRel = ri === resourceRelations.length - 1;
          const relPrefix = isLastRel ? '  │   └──' : '  │   ├──';
          text += `  │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // TIMING RELATIONSHIPS
      // ============================================================
      let hasTiming = false;
      const timingRelations = [];
      
      if (op.standardTime || op.stdTime) {
        timingRelations.push({ left: 'Standard Time', right: `${op.standardTime || op.stdTime} min`, icon: '⏱️' });
        hasTiming = true;
      }
      if (op.setupTime || op.setup) {
        timingRelations.push({ left: 'Setup Time', right: `${op.setupTime || op.setup} min`, icon: '🔧' });
        hasTiming = true;
      }
      if (op.queTime) {
        timingRelations.push({ left: 'Queue Time', right: `${op.queTime} min`, icon: '⏳' });
        hasTiming = true;
      }
      if (op.moveTime) {
        timingRelations.push({ left: 'Move Time', right: `${op.moveTime} min`, icon: '🚚' });
        hasTiming = true;
      }
      if (op.machineRunTime) {
        timingRelations.push({ left: 'Machine Run Time', right: `${op.machineRunTime} min`, icon: '🔄' });
        hasTiming = true;
      }
      if (op.directLaborTime) {
        timingRelations.push({ left: 'Direct Labor Time', right: `${op.directLaborTime} min`, icon: '👷' });
        hasTiming = true;
      }
      
      if (hasTiming) {
        text += `  │   ├── ⏱️ TIMING RELATIONSHIPS\n`;
        timingRelations.forEach((rel, ri) => {
          const isLastRel = ri === timingRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // MATERIAL RELATIONSHIPS
      // ============================================================
      let hasMaterials = false;
      const materialRelations = [];
      
      if (op.consumes) {
        materialRelations.push({ left: 'Consumes', right: op.consumes, icon: '📦' });
        hasMaterials = true;
      }
      if (op.indirectConsumables) {
        materialRelations.push({ left: 'Indirect Consumables', right: op.indirectConsumables, icon: '🧪' });
        hasMaterials = true;
      }
      if (op.byproducts) {
        materialRelations.push({ left: 'Byproducts', right: op.byproducts, icon: '♻️' });
        hasMaterials = true;
      }
      if (op.scrap) {
        materialRelations.push({ left: 'Scrap', right: op.scrap, icon: '🗑️' });
        hasMaterials = true;
      }
      
      if (hasMaterials) {
        text += `  │   ├── 📦 MATERIAL RELATIONSHIPS\n`;
        materialRelations.forEach((rel, ri) => {
          const isLastRel = ri === materialRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // SKILLS & TOOLS RELATIONSHIPS
      // ============================================================
      let hasSkillsTools = false;
      const skillsToolsRelations = [];
      
      if (op.tools) {
        skillsToolsRelations.push({ left: 'Tools Required', right: op.tools, icon: '🔧' });
        hasSkillsTools = true;
      }
      if (op.skills) {
        skillsToolsRelations.push({ left: 'Skills Required', right: op.skills, icon: '🎯' });
        hasSkillsTools = true;
      }
      if (op.skillRequired) {
        skillsToolsRelations.push({ left: 'Skill Level', right: op.skillRequired, icon: '📊' });
        hasSkillsTools = true;
      }
      
      if (hasSkillsTools) {
        text += `  │   ├── 🛠️ SKILLS & TOOLS RELATIONSHIPS\n`;
        skillsToolsRelations.forEach((rel, ri) => {
          const isLastRel = ri === skillsToolsRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // QUALITY & INSTRUCTION RELATIONSHIPS
      // ============================================================
      let hasQuality = false;
      const qualityRelations = [];
      
      if (op.workInstruction) {
        qualityRelations.push({ left: 'Work Instruction', right: op.workInstruction, icon: '📄' });
        hasQuality = true;
      }
      if (op.inspection) {
        qualityRelations.push({ left: 'Inspection', right: op.inspection, icon: '🔍' });
        hasQuality = true;
      }
      if (op.qualityPlanRef) {
        qualityRelations.push({ left: 'Quality Plan', right: op.qualityPlanRef, icon: '✅' });
        hasQuality = true;
      }
      if (op.ewiRef) {
        qualityRelations.push({ left: 'EWI Document', right: op.ewiRef, icon: '📋' });
        hasQuality = true;
      }
      if (op.controlKey) {
        qualityRelations.push({ left: 'Control Key', right: op.controlKey, icon: '🔑' });
        hasQuality = true;
      }
      if (op.parameter) {
        qualityRelations.push({ left: 'Process Parameter', right: op.parameter, icon: '⚡' });
        hasQuality = true;
      }
      
      if (hasQuality) {
        text += `  │   ├── 📋 QUALITY & INSTRUCTION RELATIONSHIPS\n`;
        qualityRelations.forEach((rel, ri) => {
          const isLastRel = ri === qualityRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // LOGISTICS RELATIONSHIPS
      // ============================================================
      let hasLogistics = false;
      const logisticsRelations = [];
      
      if (op.sourceLocation) {
        logisticsRelations.push({ left: 'Source Location', right: op.sourceLocation, icon: '📍' });
        hasLogistics = true;
      }
      if (op.output) {
        logisticsRelations.push({ left: 'Output', right: op.output, icon: '📤' });
        hasLogistics = true;
      }
      if (op.directMBOM) {
        logisticsRelations.push({ left: 'Direct MBOM', right: op.directMBOM, icon: '📦' });
        hasLogistics = true;
      }
      
      if (hasLogistics) {
        text += `  │   ├── 🔗 LOGISTICS RELATIONSHIPS\n`;
        logisticsRelations.forEach((rel, ri) => {
          const isLastRel = ri === logisticsRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // REFERENCE RELATIONSHIPS
      // ============================================================
      let hasRefs = false;
      const refRelations = [];
      
      if (op.mbomRef) {
        refRelations.push({ left: 'MBOM Reference', right: op.mbomRef, icon: '📌' });
        hasRefs = true;
      }
      if (op.ebomRef) {
        refRelations.push({ left: 'EBOM Reference', right: op.ebomRef, icon: '📌' });
        hasRefs = true;
      }
      if (op.precedes) {
        refRelations.push({ left: 'Precedes Operation', right: op.precedes, icon: '➡️' });
        hasRefs = true;
      }
      
      if (hasRefs) {
        text += `  │   ├── 🔗 REFERENCE RELATIONSHIPS\n`;
        refRelations.forEach((rel, ri) => {
          const isLastRel = ri === refRelations.length - 1;
          const relPrefix = isLastRel ? '  │   │   └──' : '  │   │   ├──';
          text += `  │   │   ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
        text += `  │   │\n`;
      }
      
      // ============================================================
      // CLASSIFICATION RELATIONSHIPS
      // ============================================================
      let hasClassification = false;
      const classificationRelations = [];
      
      if (op.operationType) {
        classificationRelations.push({ left: 'Operation Type', right: op.operationType, icon: '🏷️' });
        hasClassification = true;
      }
      if (op.category) {
        classificationRelations.push({ left: 'Category', right: op.category, icon: '📂' });
        hasClassification = true;
      }
      if (op.subCategory) {
        classificationRelations.push({ left: 'Sub Category', right: op.subCategory, icon: '📁' });
        hasClassification = true;
      }
      if (op.functionalGroup) {
        classificationRelations.push({ left: 'Functional Group', right: op.functionalGroup, icon: '📊' });
        hasClassification = true;
      }
      if (op.plant) {
        classificationRelations.push({ left: 'Plant', right: op.plant, icon: '🏭' });
        hasClassification = true;
      }
      if (op.process) {
        classificationRelations.push({ left: 'Process', right: op.process, icon: '🔄' });
        hasClassification = true;
      }
      
      if (hasClassification) {
        text += `  │   └── 📂 CLASSIFICATION RELATIONSHIPS\n`;
        classificationRelations.forEach((rel, ri) => {
          const isLastRel = ri === classificationRelations.length - 1;
          const relPrefix = isLastRel ? '  │       └──' : '  │       ├──';
          text += `  │       ${relPrefix} ${rel.icon} [${rel.left}] → ${rel.right}\n`;
        });
      }
      
      text += `  │\n`;
    });
    
    if (truncateForPreview && safeAllOperations.length > MAX_PREVIEW) {
      text += `  └── ... and ${safeAllOperations.length - MAX_PREVIEW} more operations\n\n`;
    }
    
    // ============================================================
    // SECTION 5: BOE EQUIPMENT RELATIONSHIPS
    // ============================================================
    text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
    text += '│  🏢 BOE EQUIPMENT RELATIONSHIPS                                                                          │\n';
    text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
    
    Object.entries(safeTechGroups).forEach(([tech, eqs]) => {
      if (eqs.length > 0) {
        text += `  ${tech}: ${eqs.length} equipment nodes\n`;
        eqs.forEach((eq, eqIndex) => {
          const isLastEq = eqIndex === eqs.length - 1;
          const eqPrefix = isLastEq ? '  └──' : '  ├──';
          text += `  ${eqPrefix} 📍 ${eq.name || 'Unnamed'}\n`;
          text += `  │      ├── [level] → ${eq.level || 'WorkUnit'}\n`;
          text += `  │      ├── [technology] → ${eq.technology || 'Conventional'}\n`;
          if (eq.costRate) text += `  │      ├── [cost rate] → $${eq.costRate}/h\n`;
          if (eq.capacity) text += `  │      ├── [capacity] → ${eq.capacity} hours\n`;
          if (eq.parent) text += `  │      └── [parent] → ${eq.parent}\n`;
          text += `  │\n`;
        });
      }
    });
    text += '\n';

    // ============================================================
    // SECTION 6: QUALITY PLANS RELATIONSHIPS
    // ============================================================
    if (safeQualityPlans.length > 0) {
      text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
      text += '│  📋 QUALITY PLANS RELATIONSHIPS                                                                         │\n';
      text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
      
      safeQualityPlans.forEach((qp, qpIndex) => {
        const isLastQp = qpIndex === safeQualityPlans.length - 1;
        const qpPrefix = isLastQp ? '  └──' : '  ├──';
        text += `  ${qpPrefix} 📋 ${qp.name || 'Unnamed'}\n`;
        text += `  │      ├── [target operation] → ${qp.targetOperation || 'N/A'}\n`;
        text += `  │      ├── [frequency] → ${qp.inspectionFrequency || 'N/A'}\n`;
        text += `  │      ├── [characteristics] → ${(qp.characteristics || []).length} defined\n`;
        
        (qp.characteristics || []).forEach((qc, qcIndex) => {
          const isLastQc = qcIndex === (qp.characteristics || []).length - 1;
          const qcPrefix = isLastQc ? '  │      └──' : '  │      ├──';
          let qcLine = `  │      ${qcPrefix} ${qc.name || 'Unnamed'}`;
          if (qc.tolerance) qcLine += ` [tolerance: ${qc.tolerance}]`;
          if (qc.method) qcLine += ` [method: ${qc.method}]`;
          if (qc.frequency) qcLine += ` [frequency: ${qc.frequency}]`;
          text += qcLine + '\n';
        });
        text += `  │\n`;
      });
      text += '\n';
    }

    // ============================================================
    // SECTION 7: EWI DOCUMENTS RELATIONSHIPS
    // ============================================================
    if (safeEwiDocs.length > 0) {
      text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
      text += '│  📄 EWI DOCUMENTS RELATIONSHIPS                                                                         │\n';
      text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
      
      safeEwiDocs.forEach((ewi, ewiIndex) => {
        const isLastEwi = ewiIndex === safeEwiDocs.length - 1;
        const ewiPrefix = isLastEwi ? '  └──' : '  ├──';
        text += `  ${ewiPrefix} 📄 ${ewi.name || ewi.id || 'Unnamed'}\n`;
        text += `  │      ├── [type] → ${ewi.type || 'PDF'}\n`;
        text += `  │      ├── [revision] → ${ewi.revision || 'A'}\n`;
        text += `  │      ├── [target operation] → ${ewi.targetOperation || 'N/A'}\n`;
        if (ewi.author) text += `  │      ├── [author] → ${ewi.author}\n`;
        if (ewi.approvalDate) text += `  │      └── [approval date] → ${ewi.approvalDate}\n`;
        text += `  │\n`;
      });
      text += '\n';
    }

    // ============================================================
    // SECTION 8: SUMMARY STATISTICS
    // ============================================================
    text += '┌────────────────────────────────────────────────────────────────────────────────────────────────────────────┐\n';
    text += '│  📊 SUMMARY STATISTICS                                                                                    │\n';
    text += '└────────────────────────────────────────────────────────────────────────────────────────────────────────────┘\n';
    text += `  ├── Total Operations: ${safeAllOperations.length}\n`;
    text += `  ├── Functional Groups: ${safeFunctionalGroups.length}\n`;
    text += `  ├── EBOM Components: ${safeEbom.length}\n`;
    text += `  ├── MBOM Components: ${safeMbom.length}\n`;
    text += `  ├── Equipment: ${safeEquip.length}\n`;
    text += `  ├── Tools: ${safeTools.length}\n`;
    text += `  ├── Skills: ${safeSkills.length}\n`;
    text += `  ├── Quality Plans: ${safeQualityPlans.length}\n`;
    text += `  ├── EWI Documents: ${safeEwiDocs.length}\n`;
    text += `  ├── Total Standard Time: ${analytics.totalStdTime.toFixed(1)} min\n`;
    text += `  ├── Total Setup Time: ${analytics.totalSetupTime.toFixed(1)} min\n`;
    text += `  ├── Average Std Time: ${analytics.avgStdTime.toFixed(1)} min/op\n`;
    text += `  ├── Avg Ops per FG: ${analytics.avgOpsPerFG.toFixed(1)}\n`;
    text += `  ├── Quality Coverage: ${analytics.qualityCoverage.toFixed(1)}%\n`;
    text += `  ├── EWI Coverage: ${analytics.ewiCoverage.toFixed(1)}%\n`;
    text += `  ├── Make Ratio: ${analytics.makeRatio.toFixed(1)}% Make\n`;
    text += `  └── Buy Ratio: ${(100 - analytics.makeRatio).toFixed(1)}% Buy\n`;
    
    return text;
  }, [wp, prod, safeEbom, safeMbom, safeAllOperations, safeFunctionalGroups, safeTechGroups, safeQualityPlans, safeEwiDocs, safeEquip, safeTools, safeSkills, analytics]);

  // ============================================================
  // HTML REPORT GENERATION
  // ============================================================
  const generateHTMLReport = useCallback(() => {
    const dendroText = generateDendrographText(false);
    
    let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>PLMXML Studio Complete Report</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #0a0e17; color: #e2e8f0; padding: 20px; max-width: 1400px; margin: 0 auto; }
    h1 { color: #60a5fa; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
    h2 { color: #94a3b8; margin-top: 25px; border-bottom: 1px solid #1e293b; padding-bottom: 8px; }
    h3 { color: #60a5fa; margin-top: 20px; }
    .card { background: #1a2235; border: 1px solid #1e293b; border-radius: 7px; padding: 15px; margin-bottom: 15px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
    .stat { text-align: center; padding: 10px; background: #111827; border-radius: 5px; border: 1px solid #1e293b; }
    .stat-value { font-size: 24px; font-weight: 700; color: #60a5fa; }
    .stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { text-align: left; padding: 6px 8px; color: #94a3b8; font-size: 10px; text-transform: uppercase; border-bottom: 2px solid #1e293b; }
    td { padding: 4px 8px; border-bottom: 1px solid #1e293b; }
    .badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
    .badge-blue { background: #3b82f622; color: #60a5fa; }
    .badge-green { background: #10b98122; color: #34d399; }
    .badge-yellow { background: #f59e0b22; color: #fbbf24; }
    .badge-purple { background: #8b5cf622; color: #a78bfa; }
    .badge-red { background: #ef444422; color: #f87171; }
    .badge-cyan { background: #06b6d422; color: #22d3ee; }
    pre { background: #0a0e17; padding: 15px; border-radius: 4px; border: 1px solid #1e293b; overflow-x: auto; font-size: 11px; color: #94a3b8; max-height: 800px; overflow-y: auto; font-family: 'Consolas', 'Courier New', monospace; }
    .flex { display: flex; gap: 15px; flex-wrap: wrap; }
    .flex-1 { flex: 1; min-width: 200px; }
    .text-muted { color: #64748b; }
    .relation-box { background: #111827; padding: 10px; border-radius: 4px; border-left: 3px solid #3b82f6; margin-bottom: 8px; }
    .relation-left { color: #60a5fa; font-weight: 600; }
    .relation-arrow { color: #f59e0b; margin: 0 8px; }
    .relation-right { color: #34d399; font-weight: 600; }
    .section-header { background: #1a2235; padding: 8px 12px; border-radius: 4px; border: 1px solid #3b82f644; margin-bottom: 10px; }
  </style>
</head>
<body>
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
    <div>
      <h1>🏭 PLMXML Studio - Complete Relational Report</h1>
      <p style="color: #64748b; margin: 0;">Generated: ${new Date().toLocaleString()} | Version: 15.0</p>
    </div>
    <div style="text-align: right; color: #64748b; font-size: 12px;">
      <span>Teamcenter 2512</span>
    </div>
  </div>
  
  <div class="card">
    <h2>📋 Product & Work Package</h2>
    <div class="flex">
      <div class="flex-1">
        <div class="text-muted">Product Name</div>
        <div style="font-size: 18px; font-weight: 600;">${esc(prod?.name || 'N/A')}</div>
        <div class="text-muted" style="margin-top: 5px;">${esc(prod?.description || 'No description')}</div>
      </div>
      <div class="flex-1">
        <div class="text-muted">Work Package</div>
        <div style="font-size: 16px; font-weight: 600;">${esc(wp?.name || 'N/A')}</div>
        <div style="font-size: 12px; color: #94a3b8;">Plant: ${esc(wp?.plant || 'N/A')} | Owner: ${esc(wp?.owner || 'N/A')}</div>
      </div>
      <div class="flex-1">
        <div class="text-muted">Revisions</div>
        <div>Product: <span class="badge badge-blue">${prod?.revision || 'A'}</span></div>
        <div>MBOM: <span class="badge badge-green">${prod?.mbomRev || '000'}</span></div>
        <div>BOP: <span class="badge badge-yellow">${bopData?.productBOP?.revision || 'A'}</span></div>
      </div>
    </div>
  </div>
  
  <div class="card">
    <h2>📊 Summary Statistics</h2>
    <div class="grid">
      <div class="stat"><div class="stat-value">${analytics.totalOperations}</div><div class="stat-label">Operations</div></div>
      <div class="stat"><div class="stat-value">${analytics.totalEBOM}</div><div class="stat-label">EBOM</div></div>
      <div class="stat"><div class="stat-value">${analytics.totalMBOM}</div><div class="stat-label">MBOM</div></div>
      <div class="stat"><div class="stat-value">${analytics.totalEquipment}</div><div class="stat-label">Equipment</div></div>
      <div class="stat"><div class="stat-value">${analytics.totalTools}</div><div class="stat-label">Tools</div></div>
      <div class="stat"><div class="stat-value">${analytics.totalSkills}</div><div class="stat-label">Skills</div></div>
      <div class="stat"><div class="stat-value">${analytics.qualityCoverage.toFixed(1)}%</div><div class="stat-label">Quality</div></div>
      <div class="stat"><div class="stat-value">${analytics.ewiCoverage.toFixed(1)}%</div><div class="stat-label">EWI</div></div>
    </div>
  </div>
  
  <div class="card">
    <h2>⚙️ Operational Analytics</h2>
    <div class="flex">
      <div class="flex-1">
        <div class="text-muted">Total Standard Time</div>
        <div style="font-size: 18px; font-weight: 600;">${analytics.totalStdTime.toFixed(1)} min</div>
        <div class="text-muted" style="margin-top: 5px;">Average: ${analytics.avgStdTime.toFixed(1)} min/op</div>
      </div>
      <div class="flex-1">
        <div class="text-muted">Total Setup Time</div>
        <div style="font-size: 18px; font-weight: 600;">${analytics.totalSetupTime.toFixed(1)} min</div>
        <div class="text-muted" style="margin-top: 5px;">Avg Ops/FG: ${analytics.avgOpsPerFG.toFixed(1)}</div>
      </div>
      <div class="flex-1">
        <div class="text-muted">Make/Buy Ratio</div>
        <div style="font-size: 18px; font-weight: 600;">${analytics.makeRatio.toFixed(1)}% Make</div>
        <div style="font-size: 12px; color: #94a3b8;">${analytics.makeCount} Make | ${analytics.buyCount} Buy</div>
      </div>
    </div>
  </div>
  
  <div class="card">
    <h2>🏭 Top Work Centers</h2>
    <table>
      <thead><tr><th>Work Center</th><th>Operations</th><th>Utilization</th></tr></thead>
      <tbody>
        ${analytics.topWCs.map(([wc, count]) => `
          <tr><td>${esc(wc)}</td><td>${count}</td><td>${(count / analytics.totalOperations * 100).toFixed(1)}%</td></tr>
        `).join('')}
      </tbody>
    </table>
  </div>
  
  <div class="flex">
    <div class="flex-1 card" style="margin-right: 10px;">
      <h3>🛠️ Top Skills</h3>
      <table>
        <thead><tr><th>Skill</th><th>Occurrences</th></tr></thead>
        <tbody>
          ${analytics.topSkills.map(([skill, count]) => `
            <tr><td>${esc(skill)}</td><td>${count}</td></tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    <div class="flex-1 card">
      <h3>🔧 Top Tools</h3>
      <table>
        <thead><tr><th>Tool</th><th>Occurrences</th></tr></thead>
        <tbody>
          ${analytics.topTools.map(([tool, count]) => `
            <tr><td>${esc(tool)}</td><td>${count}</td></tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  </div>
  
  <div class="card">
    <h2>🏗️ Equipment by Technology</h2>
    <div class="flex">
      ${Object.entries(safeTechGroups).filter(([_, eqs]) => eqs.length > 0).map(([tech, eqs]) => `
        <div class="flex-1" style="background: #111827; padding: 10px; border-radius: 4px; border: 1px solid #1e293b;">
          <div style="font-weight: 600; color: ${T[tech] || T.t3};">${tech}</div>
          <div style="font-size: 12px; color: #94a3b8;">${eqs.length} equipment</div>
          <div style="font-size: 11px; color: #64748b; margin-top: 5px;">
            ${eqs.map(eq => esc(eq.name || 'Unnamed')).join(', ')}
          </div>
        </div>
      `).join('')}
    </div>
  </div>
  
  <div class="card">
    <h2>🌳 Digital Thread Dendrograph - Relational Structure</h2>
    <pre>${esc(dendroText)}</pre>
  </div>
  
  <div class="card" style="border: 1px solid #8b5cf644;">
    <h2>🚀 Transformation Insights</h2>
    <div style="font-size: 13px; line-height: 1.8; color: #94a3b8;">
      <p><strong>💡 Product Structure Transformation:</strong> The digital thread represents a complete product definition from engineering to manufacturing. The EBOM (${analytics.totalEBOM} components) transforms through MBOM (${analytics.totalMBOM} components) into executable operations (${analytics.totalOperations}).</p>
      
      <p><strong>🔗 Relations & Correlations:</strong>
        <br>• ${analytics.totalOperations} operations are organized into ${analytics.totalFGs} functional groups
        <br>• ${analytics.totalEquipment} equipment items support the manufacturing process
        <br>• ${analytics.totalTools} tools and ${analytics.totalSkills} skills enable quality execution
        <br>• ${analytics.qualityCoverage.toFixed(1)}% of operations have quality plans
        <br>• ${analytics.ewiCoverage.toFixed(1)}% of operations have work instructions
      </p>
      
      <p><strong>🏭 Real-World Application:</strong> This BOM/BOP structure can be directly deployed to:
        <br>• MES systems for shop floor execution
        <br>• ERP systems for material planning and costing
        <br>• Quality management systems for inspection and compliance
        <br>• Maintenance systems for equipment and tooling management
      </p>
      
      <p><strong>📊 Analytics Summary:</strong>
        <br>• Total standard time: ${analytics.totalStdTime.toFixed(1)} minutes
        <br>• Average operations per FG: ${analytics.avgOpsPerFG.toFixed(1)}
        <br>• Make/Buy ratio: ${analytics.makeRatio.toFixed(1)}% Make / ${(100 - analytics.makeRatio).toFixed(1)}% Buy
        <br>• Top work center: ${analytics.topWCs[0]?.[0] || 'N/A'} (${analytics.topWCs[0]?.[1] || 0} operations)
        <br>• Quality Plans: ${analytics.totalQualityPlans}
        <br>• EWI Documents: ${analytics.totalEWI}
      </p>
    </div>
  </div>
  
  <div style="text-align: center; padding: 20px; color: #64748b; font-size: 11px; border-top: 1px solid #1e293b; margin-top: 20px;">
    Generated by PLMXML Studio v15.0 • Teamcenter 2512 Compatible
  </div>
</body>
</html>`;
    
    return html;
  }, [prod, wp, safeTechGroups, analytics, generateDendrographText, bopData]);

  // ============================================================
  // PDF REPORT GENERATION
  // ============================================================
  const generatePDFReport = useCallback(async () => {
    setGenerating(true);
    setProgress(0);
    
    try {
      const doc = new jsPDF('l', 'mm', 'a3'); // Landscape for better width
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 10;
      let y = margin;
      
      const addText = (text, fontSize = 8, color = [180, 180, 200]) => {
        doc.setFontSize(fontSize);
        doc.setTextColor(color[0], color[1], color[2]);
        const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
        lines.forEach(line => {
          if (y > doc.internal.pageSize.getHeight() - 15) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin + 2, y);
          y += 4;
        });
        y += 2;
      };
      
      const addSection = (title, content, tableData = null) => {
        if (y > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          y = margin;
        }
        
        doc.setFontSize(14);
        doc.setTextColor(60, 130, 250);
        doc.text(title, margin, y);
        y += 7;
        
        if (content) {
          if (Array.isArray(content)) {
            content.forEach(line => {
              if (y > doc.internal.pageSize.getHeight() - 15) {
                doc.addPage();
                y = margin;
              }
              doc.setFontSize(8);
              doc.setTextColor(180, 180, 200);
              doc.text(line, margin + 2, y);
              y += 4;
            });
          } else if (typeof content === 'string') {
            doc.setFontSize(8);
            doc.setTextColor(180, 180, 200);
            const lines = doc.splitTextToSize(content, pageWidth - margin * 2);
            lines.forEach(line => {
              if (y > doc.internal.pageSize.getHeight() - 15) {
                doc.addPage();
                y = margin;
              }
              doc.text(line, margin + 2, y);
              y += 4;
            });
          }
        }
        
        y += 4;
        
        if (tableData && tableData.length > 0) {
          const headers = Object.keys(tableData[0]);
          const rows = tableData.map(row => Object.values(row));
          
          autoTable(doc, {
            startY: y,
            head: [headers],
            body: rows,
            theme: 'dark',
            styles: { 
              fontSize: 6, 
              textColor: [200, 200, 220],
              lineColor: [30, 40, 60],
              lineWidth: 0.1
            },
            headStyles: { 
              fillColor: [30, 40, 60], 
              textColor: [150, 180, 220],
              fontSize: 6,
              fontStyle: 'bold'
            },
            alternateRowStyles: { fillColor: [15, 20, 30] },
            margin: { left: margin, right: margin }
          });
          y = doc.lastAutoTable.finalY + 8;
        }
      };
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(60, 130, 250);
      doc.text('🏭 PLMXML Studio - Relational Report', margin, y);
      y += 8;
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 140);
      doc.text(`Generated: ${new Date().toLocaleString()} | Teamcenter 2512 Compatible`, margin, y);
      y += 10;
      
      // Product Info
      addSection('📋 Product & Work Package', [
        `Product: ${prod?.name || 'N/A'}`,
        `Description: ${prod?.description || 'No description'}`,
        `Work Package: ${wp?.name || 'N/A'}`,
        `Plant: ${wp?.plant || 'N/A'} | Owner: ${wp?.owner || 'N/A'}`
      ]);
      
      // Statistics
      const statsData = [
        ['Metric', 'Value'],
        ['Operations', analytics.totalOperations],
        ['EBOM', analytics.totalEBOM],
        ['MBOM', analytics.totalMBOM],
        ['Equipment', analytics.totalEquipment],
        ['Tools', analytics.totalTools],
        ['Skills', analytics.totalSkills],
        ['Quality Coverage', `${analytics.qualityCoverage.toFixed(1)}%`],
        ['EWI Coverage', `${analytics.ewiCoverage.toFixed(1)}%`]
      ];
      addSection('📊 Summary Statistics', null, statsData.slice(1));
      
      // Dendrograph
      const dendroText = generateDendrographText(false);
      addSection('🌳 Digital Thread Dendrograph', dendroText);
      
      doc.save(`PLMXML_Relational_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      setProgress(100);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(`Error generating PDF: ${error.message}`);
    }
    
    setGenerating(false);
  }, [prod, wp, analytics, generateDendrographText]);

  // ============================================================
  // EXCEL REPORT GENERATION
  // ============================================================
  const generateExcelReport = useCallback(() => {
    try {
      const wb = XLSX.utils.book_new();
      
      // Summary sheet
      const summaryData = [
        ['Metric', 'Value', 'Description'],
        ['Total Operations', analytics.totalOperations, 'All operations in BOP'],
        ['Functional Groups', analytics.totalFGs, 'All functional groups'],
        ['EBOM Components', analytics.totalEBOM, 'Engineering BOM items'],
        ['MBOM Components', analytics.totalMBOM, 'Manufacturing BOM items'],
        ['Equipment', analytics.totalEquipment, 'Total equipment nodes'],
        ['Tools', analytics.totalTools, 'Tooling resources'],
        ['Skills', analytics.totalSkills, 'Skill resources'],
        ['Quality Plans', analytics.totalQualityPlans, 'Quality plans'],
        ['EWI Documents', analytics.totalEWI, 'Work instructions'],
        ['Quality Coverage', `${analytics.qualityCoverage.toFixed(1)}%`, 'Ops with quality plans'],
        ['EWI Coverage', `${analytics.ewiCoverage.toFixed(1)}%`, 'Ops with work instructions'],
        ['Make Ratio', `${analytics.makeRatio.toFixed(1)}%`, 'Make vs Buy ratio'],
        ['Total Standard Time', analytics.totalStdTime.toFixed(1), 'Minutes'],
        ['Avg Ops/FG', analytics.avgOpsPerFG.toFixed(1), 'Operations per functional group']
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');
      
      // Operations with all details
      const opData = safeAllOperations.map(op => ({
        'Sequence': op.sequence || op.seq || '',
        'Name': op.name || '',
        'Description': op.description || '',
        'Work Center': op.workCenter || op.wc || '',
        'Std Time': op.standardTime || op.stdTime || 0,
        'Setup Time': op.setupTime || op.setup || 0,
        'Machine': op.machine || '',
        'Tooling': op.tooling || '',
        'Tools': Array.isArray(op.tools) ? op.tools.join(', ') : (op.tools || ''),
        'Skills': Array.isArray(op.skills) ? op.skills.join(', ') : (op.skills || ''),
        'Consumes': op.consumes || '',
        'Indirect Consumables': op.indirectConsumables || '',
        'Byproducts': op.byproducts || '',
        'Parameter': op.parameter || '',
        'Control Key': op.controlKey || '',
        'Work Instruction': op.workInstruction || '',
        'Inspection': op.inspection || '',
        'Quality Plan Ref': op.qualityPlanRef || '',
        'EWI Ref': op.ewiRef || '',
        'Source Location': op.sourceLocation || '',
        'Output': op.output || '',
        'MBOM Ref': op.mbomRef || '',
        'EBOM Ref': op.ebomRef || '',
        'Precedes': op.precedes || '',
        'Functional Group': op.functionalGroup || '',
        'Plant': op.plant || '',
        'Process': op.process || '',
        'Operation Type': op.operationType || '',
        'Category': op.category || '',
        'Sub Category': op.subCategory || ''
      }));
      if (opData.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(opData), 'Operations');
      }
      
      // EBOM
      if (safeEbom.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeEbom), 'EBOM');
      }
      
      // MBOM
      if (safeMbom.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeMbom), 'MBOM');
      }
      
      // Equipment
      if (safeEquip.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeEquip), 'Equipment');
      }
      
      // Tools
      if (safeTools.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeTools), 'Tools');
      }
      
      // Skills
      if (safeSkills.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeSkills), 'Skills');
      }
      
      XLSX.writeFile(wb, `PLMXML_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
      
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(`Error generating Excel: ${error.message}`);
    }
  }, [analytics, safeAllOperations, safeEbom, safeMbom, safeEquip, safeTools, safeSkills]);

  // ============================================================
  // DOWNLOAD FUNCTIONS
  // ============================================================
  const downloadHTMLReport = useCallback(() => {
    const html = generateHTMLReport();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `PLMXML_Relational_Report_${new Date().toISOString().slice(0,10)}.html`);
  }, [generateHTMLReport]);

  const downloadDendrograph = useCallback(() => {
    const text = generateDendrographText(false);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `Dendrograph_Relational_${new Date().toISOString().slice(0,10)}.txt`);
  }, [generateDendrographText]);

  const previewText = generateDendrographText(true);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: T.s2, borderRadius: '10px', padding: '16px', width: '95vw', maxWidth: '1200px', maxHeight: '95vh', overflow: 'auto', border: `1px solid ${T.b}`, boxShadow: '0 10px 40px rgba(0,0,0,0.8)' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: T.pl, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FileText size={18} /> Relational Report Generator
          </div>
          <button onClick={onClose} style={{ ...S.btn, ...S.bGho, padding: '3px 6px' }}>
            <X size={14} /> Close
          </button>
        </div>
        
        <div style={{ ...S.grid(4), marginBottom: '12px' }}>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalOperations}</div><div style={S.statLbl}>Ops</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalEBOM}</div><div style={S.statLbl}>EBOM</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalMBOM}</div><div style={S.statLbl}>MBOM</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalEquipment}</div><div style={S.statLbl}>Equip</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalTools}</div><div style={S.statLbl}>Tools</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.totalSkills}</div><div style={S.statLbl}>Skills</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.qualityCoverage.toFixed(0)}%</div><div style={S.statLbl}>Quality</div></div>
          <div style={S.statCard}><div style={S.statVal}>{analytics.ewiCoverage.toFixed(0)}%</div><div style={S.statLbl}>EWI</div></div>
        </div>
        
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <button 
            onClick={generatePDFReport} 
            style={{ ...S.btn, ...S.bDan }}
            disabled={generating}
          >
            <FileText size={14} /> {generating ? 'Generating...' : 'PDF Report'}
          </button>
          <button 
            onClick={generateExcelReport} 
            style={{ ...S.btn, ...S.bSuc }}
            disabled={generating}
          >
            <FileSpreadsheet size={14} /> Excel Report
          </button>
          <button 
            onClick={downloadHTMLReport} 
            style={{ ...S.btn, ...S.bPri }}
            disabled={generating}
          >
            <FileCode size={14} /> HTML Report
          </button>
          <button 
            onClick={downloadDendrograph} 
            style={{ ...S.btn, ...S.bPP }}
            disabled={generating}
          >
            <GitMerge size={14} /> Dendrograph (.txt)
          </button>
        </div>
        
        {generating && (
          <div style={{ margin: '8px 0' }}>
            <div style={S.progressBar}>
              <div style={{ ...S.progressFill, width: `${progress}%`, background: `linear-gradient(90deg, ${T.p}, ${T.pp})` }} />
            </div>
            <div style={{ fontSize: '0.6rem', color: T.t3, textAlign: 'center' }}>
              Generating report... {progress}%
            </div>
          </div>
        )}
        
        <div style={{ marginTop: '12px', maxHeight: '400px', overflow: 'auto', background: T.bg, borderRadius: '4px', padding: '8px', border: `1px solid ${T.b}` }}>
          <div style={{ fontSize: '0.6rem', color: T.t3, fontWeight: 600, marginBottom: '4px' }}>
            📄 Dendrograph Preview (Shows relational Left → Right structure)
          </div>
          <pre style={{ fontSize: '0.55rem', color: T.t2, margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: '1.4' }}>
            {previewText}
          </pre>
        </div>
        
      </div>
    </div>
  );
}